import React, {useEffect} from "react";
import styled from 'styled-components';
import {Modal as BootstrapModal} from 'react-bootstrap'

interface Props {
    titleText: string | null
    onTitleClick?: () => void

    isShowing: boolean
    isPreventClose?: boolean
    size?: 'sm' | 'md' | 'lg' | 'xl'

    onClose: () => void
    body: React.ReactNode
}

const Modal: React.FC<Props> = ({isShowing, onClose, titleText, onTitleClick, body, isPreventClose, size}) => {
    useEffect(() => {
        if (!isShowing) return

        const scrollY = window.scrollY
        document.body.style.position = 'fixed'
        document.body.style.top = `-${scrollY}px`
        document.body.style.width = '100%'

        let startY = 0

        const handleTouchStart = (e: TouchEvent) => {
            startY = e.touches[0].clientY
        }

        const handleTouchMove = (e: TouchEvent) => {
            const modalBody = (e.target as Element)?.closest?.('.modal-body')
            if (!modalBody) {
                e.preventDefault()
                return
            }

            const { scrollTop, scrollHeight, clientHeight } = modalBody
            const currentY = e.touches[0].clientY
            const isScrollingUp = currentY > startY
            const isScrollingDown = currentY < startY
            const isAtTop = scrollTop <= 0
            const isAtBottom = scrollTop + clientHeight >= scrollHeight

            if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
                e.preventDefault()
            }
        }

        document.addEventListener('touchstart', handleTouchStart, { passive: true })
        document.addEventListener('touchmove', handleTouchMove, { passive: false })

        return () => {
            document.body.style.position = ''
            document.body.style.top = ''
            document.body.style.width = ''
            window.scrollTo({ top: scrollY, behavior: 'instant' })
            document.removeEventListener('touchstart', handleTouchStart)
            document.removeEventListener('touchmove', handleTouchMove)
        }
    }, [isShowing])

    return (
        <BootstrapModalWrapper
            show={isShowing}
            onHide={onClose}
            centered
            fullscreen='md-down'
            // @ts-ignore
            size={size}

            backdrop={true}
        >
            {
                titleText !== null && (
                    <Header closeButton>
                        <Title onClick={onTitleClick} $clickable={!!onTitleClick}>{titleText}</Title>
                    </Header>
                )
            }

            <Body>{body}</Body>
        </BootstrapModalWrapper>
    )
}

const BootstrapModalWrapper = styled(BootstrapModal)`
    z-index: 9999;

    @media (max-width: 767.98px) {
        &.modal {
            overflow: hidden;
        }
    }

    .modal-content {
        border: none;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    }

    @media (max-width: 768px) {
        .modal-content {
            border-radius: 0;
            overflow: visible;
        }
    }
`

const Header = styled(BootstrapModal.Header)`
    text-align: center;
    background-color: var(--card-bg-color);
    color: var(--text-color);
    border-bottom: none;
    padding: 28px 24px 0 24px;

    .btn-close {
        opacity: 0.5;
        &:hover {
            opacity: 1;
        }
    }
`

const Title = styled.h3<{ $clickable?: boolean }>`
    width: 100%;
    padding-left: 30px;
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
`;

const Body = styled(BootstrapModal.Body)`
    background-color: var(--card-bg-color);
    color: var(--text-color);
    padding: 24px;

    @media (max-width: 767.98px) {
        height: 0;
        min-height: 0;
        flex-grow: 1;
        overflow-y: auto !important;
        overscroll-behavior: none;
        -webkit-overflow-scrolling: touch;
    }
`

export default Modal